import React, { useState, useEffect } from 'react';
import { Star, Loader2, MapPin, ChevronRight, MessageCircle, Clock, Info, ArrowRightLeft } from 'lucide-react';
import { FaWhatsapp, FaInstagram, FaFacebook } from 'react-icons/fa';
import { useTimeFormatParameters, useBusinessParameters } from '@/hooks/useParameters';
import { parseOpeningHours, isBusinessOpen, getNextOpeningTime } from '@communitly/shared';
import { shareToSocialNetwork, createShareOptions } from '@/utils/socialNetworkUtils';
import { Link } from 'react-router-dom';
import DiscountBadge from './DiscountBadge';
import ProfileBadge from './ProfileBadge';
import { BusinessBadges } from './BusinessBadges';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Review } from '@/hooks/useReviews';
import RequestDiscountButton from './RequestDiscountButton';
import { ReachOutModal } from './ReachOutModal';
import { useCommunity } from '@/contexts/CommunityContext';
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import ChatSidebar from './ChatSidebar';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { CHAT_ROLE } from '@/utils/ChatUtils';
import ImageGallery from './ImageGallery';
import ImageModal from './ImageModal';
import BusinessInfoModal from './BusinessInfoModal';
import { useIsMobile } from '@/hooks/use-mobile';
import { WeeklyHours } from '@communitly/shared';
import { ServiceDescriptionCard, ReviewsCard } from '@/components/service';
import { BusinessImageGallery } from '@/components/ui/business-image-gallery';
import BusinessInfoPanel from './BusinessInfoPanel';

interface ServiceImage {
  id: string;
  url: string;
}

interface SocialLinks {
  instagram?: string | null;
  facebook?: string | null;
  tiktok?: string | null;
}

export interface ServiceDetailsProps {
  title?: string;
  name?: string;
  description: string;
  images?: ServiceImage[];
  ownerName?: string;
  ownerImageUrl?: string;
  isVerified?: boolean;
  rating: number;
  reviewCount: number;
  price: number | string | 'Free';
  location: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    county?: string | null;
    postalCode?: string | null;
    country?: string | null;
  };
  businessId: string;
  socialLinks?: SocialLinks;
  imageUrl?: string;
  companyLogo?: string | null;
  googleMapsUrl?: string | null;
  reviews?: Review[];
  reviewsLoading?: boolean;
  reviewsError?: Error | null;
  isBusinessOwner?: boolean;
  userReview?: Review | null;
  createReview?: (businessId: string, rating: number, comment: string) => Promise<void>;
  updateReview?: (reviewId: string, rating: number, comment: string) => Promise<void>;
  deleteReview?: (reviewId: string) => Promise<void>;
  discount?: {
    id: string;
    amount: number;
    isPercentage: boolean;
    appliesToAll: boolean;
    type?: 'Paid' | 'Free';
    description?: string | null;
    coupon_code?: string | null;
  } | null;
  isAuthenticated?: boolean;
  workImages?: string[];
  openingHours?: string;
}

const ServiceDetails: React.FC<ServiceDetailsProps> = ({
  title,
  name,
  description,
  images = [],
  ownerName,
  ownerImageUrl,
  isVerified = false,
  rating,
  reviewCount,
  price,
  location,
  contactEmail,
  contactPhone,
  website,
  address,
  businessId,
  socialLinks,
  imageUrl,
  companyLogo,
  googleMapsUrl,
  reviews,
  reviewsLoading,
  reviewsError,
  isBusinessOwner,
  userReview,
  createReview,
  updateReview,
  deleteReview,
  discount,
  isAuthenticated = false,
  workImages = [],
  openingHours
}) => {
  const { getParameter } = useCommunity();
  const { t } = useTranslation();
  const [chatOpen, setChatOpen] = useState(false);
  const [existingConversation, setExistingConversation] = useState<boolean>(false);
  const [checkingConversation, setCheckingConversation] = useState<boolean>(false);
  const [conversationError, setConversationError] = useState<Error | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [hoursModalOpen, setHoursModalOpen] = useState(false);
  const [reachOutModalOpen, setReachOutModalOpen] = useState(false);
  const displayTitle = title || name || t('service.card.unnamed');
  const currency = getParameter<string>('currency') || '$';
  const { toast } = useToast();
  const { user } = useAuth();
  const { checkExistingConversation } = useChat();
  const isMobile = useIsMobile();
  const { timeDisplayFormat } = useTimeFormatParameters();
  const { defaultBannerUrl } = useBusinessParameters();

  // Parse opening hours from string to WeeklyHours object
  const parsedOpeningHours = typeof openingHours === 'string' 
    ? parseOpeningHours(openingHours) 
    : openingHours;

  // Calculate opening status for display with translation
  const openingStatus = isBusinessOpen(parsedOpeningHours, timeDisplayFormat, t);
  
  // Enhanced status with next opening time when closed
  const getEnhancedOpeningStatus = () => {
    if (!parsedOpeningHours) {
      return {
        isOpen: false,
        status: t('service.details.opening_status.hours_unavailable')
      };
    }
    
    if (openingStatus.isOpen) {
      return openingStatus; // Already shows "Open now • Closes at 6PM"
    }
    
    // If closed, get next opening time
    const nextOpeningTime = getNextOpeningTime(parsedOpeningHours, timeDisplayFormat, t);
    return {
      isOpen: false,
      status: nextOpeningTime // "Opens Monday at 9AM"
    };
  };
  
  const enhancedOpeningStatus = getEnhancedOpeningStatus();


  // Get desktop opening status badge with subtle styling
  const getDesktopOpeningBadge = () => {
    if (!parsedOpeningHours) return null;
    
    return {
      text: openingStatus.status,
      className: openingStatus.isOpen 
        ? "bg-green-100 text-green-800 text-sm px-2 py-1" 
        : "bg-gray-100 text-gray-600 text-sm px-2 py-1"
    };
  };

  const desktopOpeningBadge = getDesktopOpeningBadge();

  // Check conversation status only when user is authenticated and it's not their business
  useEffect(() => {
    if (isAuthenticated && businessId && !isBusinessOwner) {
      console.log('[ServiceDetails] Checking for existing conversation with business ID:', businessId);
      checkConversationStatus();
    }
  }, [isAuthenticated, businessId, isBusinessOwner]);

  const checkConversationStatus = async () => {
    setCheckingConversation(true);
    setConversationError(null);
    
    try {
      // This will now clear state for the new business context without affecting display
      const conversation = await checkExistingConversation(businessId, CHAT_ROLE.COMMUNITY_MEMBER);
      setExistingConversation(!!conversation);
      console.log(`[ServiceDetails] Conversation ${conversation ? 'found' : 'not found'}`);
    } catch (error) {
      console.error('[ServiceDetails] Error checking existing conversation:', error);
      setConversationError(error as Error);
      toast({
        variant: "destructive",
        title: "Error checking conversation status",
        description: "Please try again later."
      });
    } finally {
      setCheckingConversation(false);
    }
  };

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setModalOpen(true);
  };

  const handleChatButtonClick = async () => {
    if (!isAuthenticated || !businessId) return;
    
    if (chatOpen) {
      console.log('[ServiceDetails] Closing existing chat to reset state');
      setChatOpen(false);
      
      // Use a timeout to ensure the chat is fully closed before reopening
      setTimeout(() => {
        checkConversationStatus().then(() => {
          setChatOpen(true);
        });
      }, 300);
    } else {
      setChatOpen(true);
    }
  };

  const serviceImages = images && images.length > 0 
    ? images.map(img => img.url) 
    : (imageUrl ? [imageUrl] : []);
  
  // For ServiceDetails header: company_banner_url → defaultBannerUrl
  const bannerImage = imageUrl || defaultBannerUrl;
  const bannerImages = bannerImage ? [bannerImage] : [];

  const hasWorkImages = workImages.length > 0;

  const formatLocationDisplay = () => {
    if (!address) return '';
    
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.postalCode) parts.push(address.postalCode);
    
    return parts.join(', ');
  };

  const getChatButtonText = () => {
    if (checkingConversation) {
      return t('service.details.loading_button');
    }
    return t('service.details.chat_with_business');
  };

  // Social sharing handlers using the centralized utils
  const handleWhatsAppShare = () => {
    const shareOptions = createShareOptions(displayTitle, window.location.href);
    shareToSocialNetwork('whatsapp', shareOptions, toast);
  };

  const handleInstagramShare = async () => {
    // Get the best image to share (company logo or first service image)
    const imageToShare = companyLogo || imageUrl || (images && images.length > 0 ? images[0].url : null);
    
    const shareOptions = createShareOptions(
      displayTitle, 
      window.location.href, 
      imageToShare || undefined,
      formatLocationDisplay
    );
    
    await shareToSocialNetwork('instagram', shareOptions, toast);
  };

  const handleFacebookShare = async () => {
    const shareOptions = createShareOptions(
      displayTitle, 
      window.location.href, 
      undefined,
      formatLocationDisplay
    );
    
    await shareToSocialNetwork('facebook', shareOptions, toast);
  };

  // Handle request discount button click
  const handleRequestDiscountClick = () => {
    setReachOutModalOpen(true);
  };

  // Handle ReachOut modal success
  const handleReachOutSuccess = (result: { serviceRequestId: string; conversationId?: string }) => {
    console.log('ReachOut request successful:', result);
    // The modal handles the success toast, so we don't need additional handling here
  };

  // Create business data object for ReachOutModal
  const businessData = {
    id: businessId,
    name: displayTitle,
    company_logo: companyLogo,
    company_banner_url: imageUrl,
    city: address?.city,
    postal_code: address?.postalCode,
  };

  // Rating component to avoid duplication
  const RatingComponent = ({ className = "" }: { className?: string }) => (
    <div className={cn("flex items-center gap-2", className)}>
      {reviewCount > 0 && rating !== undefined ? (
        <>
          <Star className="w-5 h-5 text-yellow-400 fill-current" strokeWidth={2} />
          <span className="font-medium text-base text-gray-900">{typeof rating === 'number' ? rating.toFixed(1) : '0.0'}</span>
          <span className="text-sm text-muted-foreground">({reviewCount} {t('service.details.reviews')})</span>
        </>
      ) : (
        <>
          <Star className="w-5 h-5 text-gray-400" strokeWidth={2} />
          <span className="text-sm text-muted-foreground">{t('service.card.no_reviews')}</span>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* LinkedIn-Style Hero Section with Banner + Business Info */}
      <div className="relative">
        {/* Banner Image */}
        <div className="relative h-64 sm:h-80 md:h-96 rounded-t-xl overflow-hidden">
          <ImageGallery 
            images={bannerImages.length > 0 ? bannerImages : serviceImages}
            alt={displayTitle}
            aspectRatio="wide"
            className="h-full"
            fallbackImage={`https://placehold.co/600x400?text=${t('service.details.no_image')}`}
          />
          
          {/* Discount Badge - Mobile only */}
          {discount && (
            <div className="absolute top-4 left-4 z-10 sm:hidden">
              <DiscountBadge 
                amount={discount.amount} 
                isPercentage={discount.isPercentage} 
                appliesToAll={discount.appliesToAll}
                type={discount.type || 'Paid'}
                size="md"
                hideLabel={true}
              />
            </div>
          )}

        </div>

        {/* Business Info Section - Single unified card */}
        <div className={cn(
          "relative bg-white rounded-b-xl -mt-4 pb-6 px-6 shadow-sm border",
          companyLogo ? "pt-8" : "pt-4"
        )}>
          {/* Large Business Logo - Overlapping Banner */}
          {companyLogo && (
            <div className="absolute -top-16 left-6 z-20">
              <div className="h-32 w-32 rounded-full overflow-hidden border-4 border-white shadow-xl bg-white">
                <img 
                  src={companyLogo} 
                  alt={`${displayTitle} logo`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          {/* Social Media Share Icons - Mobile only, positioned horizontally on the far right */}
          <div className={cn(
            "absolute right-4 sm:hidden",
            companyLogo ? "top-1" : "top-4"
          )}>
            <div className="flex items-center justify-end gap-1">
              <span className="text-sm text-gray-600 font-medium">{t('service.details.share')}</span>
              
              {/* WhatsApp - Functional */}
              <button 
                onClick={handleWhatsAppShare}
                className="w-9 h-9 flex items-center justify-center transition-colors hover:opacity-70"
                aria-label="Share on WhatsApp"
              >
                <FaWhatsapp className="h-5 w-5 text-gray-600" />
              </button>

              {/* Instagram - Functional */}
              <button 
                onClick={handleInstagramShare}
                className="w-9 h-9 flex items-center justify-center transition-colors hover:opacity-70"
                aria-label="Share on Instagram"
              >
                <FaInstagram className="h-5 w-5 text-gray-600" />
              </button>

              {/* Facebook - Functional */}
              <button 
                onClick={handleFacebookShare}
                className="w-9 h-9 flex items-center justify-center transition-colors hover:opacity-70"
                aria-label="Share on Facebook"
              >
                <FaFacebook className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Business Information - Positioned below logo */}
          <div className={cn(companyLogo ? "pt-16" : "pt-12")}>
            {/* Business Name and Location - Mobile First */}
            <div className="flex flex-col">
              {/* Business Name Container - Reverted back to original size */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-0">
                  {displayTitle}
                </h1>
                
                {/* Rating - Desktop only (hidden on mobile) */}
                <RatingComponent className="hidden sm:flex sm:mt-1" />
              </div>

              {/* Location with Opening Status - Desktop shows both, Mobile shows only location */}
              {formatLocationDisplay() && (
                <div className="flex items-center gap-2 text-muted-foreground text-base mt-1">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1.5 flex-shrink-0" />
                    <span>{formatLocationDisplay()}</span>
                  </div>
                  {/* Opening Status Badge - Desktop only */}
                  {desktopOpeningBadge && (
                    <span className={`hidden sm:inline-flex rounded ${desktopOpeningBadge.className} font-medium`}>
                      {desktopOpeningBadge.text}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Rating - Mobile only (below location) */}
            <div className="sm:hidden mt-2">
              <RatingComponent />
            </div>

            {/* Business Achievement Badges */}
            <div className="mt-4">
              <BusinessBadges 
                businessId={businessId}
                mode="full"
                className="max-w-none"
              />
            </div>

            {/* Compact Opening Hours - Mobile only */}
            {parsedOpeningHours && (
              <div className="pt-3 pb-2 sm:hidden">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className={cn(
                    "text-sm font-medium",
                    enhancedOpeningStatus.isOpen ? "text-green-700" : "text-red-600"
                  )}>
                    {enhancedOpeningStatus.status}
                  </span>
                </div>
                
                {/* More information link */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {t('service.details.more_info')}
                  </span>
                  <button 
                    onClick={() => setHoursModalOpen(true)}
                    className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                    aria-label={t('service.details.view_all_hours')}
                  >
                    <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors" />
                  </button>
                </div>
              </div>
            )}

            {/* Discount and Chat Actions - Mobile-optimized, desktop unchanged */}
            <div className="pt-5 flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Request Discount Button/Sign-in - Only for paid discounts */}
              {discount && discount.type === 'Paid' && !isBusinessOwner && (
                <>
                  {isAuthenticated ? (
                    <RequestDiscountButton
                      business={businessData}
                      discount={{
                        id: discount.id,
                        amount: discount.amount,
                        isPercentage: discount.isPercentage,
                        appliesToAll: discount.appliesToAll,
                        type: discount.type,
                        description: discount.description,
                        coupon_code: discount.coupon_code
                      }}
                      onRequest={handleRequestDiscountClick}
                    />
                  ) : (
                    <>
                      {/* Mobile: Full-width sign in for quote button */}
                      <Link to={`/auth?returnTo=${encodeURIComponent(window.location.pathname)}`} className="block sm:hidden">
                        <button 
                          className={cn(
                            "w-full flex items-center justify-between",
                            "px-4 py-3",
                            "bg-orange-50 hover:bg-orange-100 active:bg-orange-200",
                            "rounded-lg transition-colors border border-orange-200",
                            "group"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <ArrowRightLeft className="h-4 w-4 text-orange-800" />
                            <span className="text-sm font-medium text-orange-800">
                              Sign in to request quote
                            </span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-orange-600 group-hover:translate-x-0.5 transition-transform" />
                        </button>
                      </Link>
                      
                      {/* Desktop: Compact sign in for quote button */}
                      <Link to={`/auth?returnTo=${encodeURIComponent(window.location.pathname)}`} className="hidden sm:inline-block">
                        <button 
                          className={cn(
                            "flex items-center gap-1.5 px-3 py-2 text-xs font-medium",
                            "bg-orange-50 hover:bg-orange-100 text-orange-800",
                            "border border-orange-200 rounded-md transition-colors"
                          )}
                        >
                          <ArrowRightLeft className="h-3 w-3" />
                          Sign in to request quote
                        </button>
                      </Link>
                    </>
                  )}
                </>
              )}
              
              {/* Chat Button - Full-width on mobile only, desktop unchanged */}
              {!isBusinessOwner && (
                <div className="w-full sm:w-auto sm:flex-shrink-0">
                  {isAuthenticated ? (
                    <>
                      {/* Mobile: Full-width button */}
                      <button 
                        onClick={handleChatButtonClick}
                        disabled={checkingConversation}
                        className={cn(
                          "w-full flex items-center justify-between sm:hidden",
                          "px-4 py-3",
                          "bg-gray-50 hover:bg-gray-100 active:bg-gray-200",
                          "rounded-lg transition-colors",
                          "group",
                          checkingConversation && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <MessageCircle className="h-4 w-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-900">
                            {getChatButtonText()}
                          </span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                      </button>
                      
                      {/* Desktop: Original compact button */}
                      <button
                        onClick={handleChatButtonClick}
                        disabled={checkingConversation}
                        className={cn(
                          "hidden sm:flex items-center gap-1.5",
                          "h-7 px-3 text-xs font-medium",
                          "bg-primary hover:bg-primary/90 text-white",
                          "rounded-md transition-colors",
                          checkingConversation && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        {getChatButtonText()}
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Mobile: Full-width sign in button */}
                      <Link to={`/auth?returnTo=${encodeURIComponent(window.location.pathname)}`} className="block sm:hidden">
                        <button 
                          className={cn(
                            "w-full flex items-center justify-between",
                            "px-4 py-3",
                            "bg-gray-50 hover:bg-gray-100 active:bg-gray-200",
                            "rounded-lg transition-colors",
                            "group"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <MessageCircle className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-900">
                              {t('service.details.sign_in_chat')}
                            </span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                        </button>
                      </Link>
                      
                      {/* Desktop: Original compact button */}
                      <Link to={`/auth?returnTo=${encodeURIComponent(window.location.pathname)}`} className="hidden sm:inline-block">
                        <button 
                          className={cn(
                            "h-7 px-3 text-xs font-medium",
                            "border border-primary text-primary",
                            "hover:bg-primary hover:text-white",
                            "rounded-md transition-colors"
                          )}
                        >
                          {t('service.details.sign_in_chat')}
                        </button>
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Two-Column Layout - Desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Modern About Card */}
          <ServiceDescriptionCard
            description={description}
          />

          {/* Work images gallery Card */}
          {hasWorkImages && (
            <Card>
              <CardHeader className="pb-0">
                <CardTitle className="text-lg font-semibold text-gray-900 leading-none">{t('service.details.our_work')}</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <BusinessImageGallery
                  images={workImages}
                  businessTitle={displayTitle}
                  onImageClick={handleImageClick}
                  className="w-full"
                />
              </CardContent>
            </Card>
          )}

          
          {/* Modern Reviews Card */}
          {reviews && (
            <ReviewsCard
              businessId={businessId}
              reviews={reviews}
              isLoading={reviewsLoading || false}
              error={reviewsError || null}
              averageRating={rating}
              reviewCount={reviewCount}
              isBusinessOwner={isBusinessOwner || false}
              userReview={userReview || null}
              createReview={createReview}
              updateReview={updateReview}
              deleteReview={deleteReview}
              isAuthenticated={isAuthenticated}
            />
          )}
        </div>

        {/* Sticky Sidebar Column - 1/3 width - Desktop only */}
        <div className="hidden lg:block">
          <div className="sticky top-6">
            <BusinessInfoPanel
              website={website}
              address={address}
              openingHours={parsedOpeningHours}
              businessName={displayTitle}
              socialLinks={socialLinks}
            />
          </div>
        </div>
      </div>

      {/* Chat sidebar */}
      {isAuthenticated && (
        <ChatSidebar
          open={chatOpen}
          onOpenChange={setChatOpen}
          counterpartId={businessId}
          counterpartName={displayTitle}
          counterpartLogo={companyLogo}
          userRole={CHAT_ROLE.COMMUNITY_MEMBER}
        />
      )}

      {/* Image Modal for viewing work images */}
      <ImageModal 
        images={workImages}
        initialIndex={selectedImageIndex}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />

      {/* Business Info Modal */}
      <BusinessInfoModal
        open={hoursModalOpen}
        onOpenChange={setHoursModalOpen}
        openingHours={parsedOpeningHours}
        businessName={displayTitle}
        address={address}
        website={website}
      />

      {/* ReachOut Modal for discount service requests */}
      <ReachOutModal
        isOpen={reachOutModalOpen}
        onClose={() => setReachOutModalOpen(false)}
        selectedBusinesses={[businessData]}
        searchParams={{}} // Empty since user will provide description
        requiresServiceDescription={true}
        onSuccess={handleReachOutSuccess}
      />
    </div>
  );
};

export default ServiceDetails;
